import { useQuery } from "@tanstack/react-query";

import useApiHelper from "../../hooks/useApiHelper";
import { IDefaultResponse } from "../../models/DefaultResponse";
import {
  IImportSyncCustomer,
  IImportSyncRunDetail,
  IImportSyncStatus,
} from "../../models/ImportSync/ImportSync";
import { ImportSyncQueryKeys } from "./keys";

/** How often the page refreshes while a sync is running, and while nothing is. */
const LIVE_REFRESH_MS = 5_000;
const IDLE_REFRESH_MS = 60_000;

/**
 * Barney answers 200 with success false when a customer's Core has nothing to give — not set up,
 * unreachable, not deployed yet. That is a state to show, so it is thrown as an error carrying the
 * reason rather than returned as empty data.
 */
function unwrap<T>(res: IDefaultResponse<T>): T {
  if (!res.success) throw new Error(res.message || "No sync data for this customer.");
  return res.data;
}

export function useImportSyncCustomers() {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ImportSyncQueryKeys.customers(),
    queryFn: () =>
      get<IDefaultResponse<IImportSyncCustomer[]>>("/importsync/customers").then(unwrap),
    staleTime: 5 * 60_000,
  });
}

export function useImportSyncStatus(customerId?: string) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ImportSyncQueryKeys.status(customerId ?? ""),
    queryFn: () =>
      get<IDefaultResponse<IImportSyncStatus>>(
        `/importsync/customer/${customerId}/status?source=Pinnacle&recent=30`,
      ).then(unwrap),
    enabled: !!customerId,
    // Polls quickly while a run is live so the progress bar moves, slowly otherwise so a
    // scheduled run starting is still noticed without anyone reloading.
    refetchInterval: (query) =>
      query.state.data?.current ? LIVE_REFRESH_MS : IDLE_REFRESH_MS,
    refetchIntervalInBackground: false,
  });
}

export function useImportSyncRun(customerId?: string, runId?: string) {
  const { get } = useApiHelper();

  return useQuery({
    queryKey: ImportSyncQueryKeys.run(customerId ?? "", runId ?? ""),
    queryFn: () =>
      get<IDefaultResponse<IImportSyncRunDetail>>(
        `/importsync/customer/${customerId}/run/${runId}`,
      ).then(unwrap),
    enabled: !!customerId && !!runId,
  });
}
